import { urlApi } from '@/utils/url';

export interface AuditActionOption {
    /** Action name as returned by the API mapper and used for i18n keys. */
    ui: string;
    /** Action name as persisted, which is what the API expects as a filter. */
    server: string;
}

export const AUDIT_ACTIONS: AuditActionOption[] = [
    { ui: 'agent_attached', server: 'queue_agent_attached' },
    { ui: 'agent_detached', server: 'queue_agent_detached' },
];

export interface AuditState {
    [key: string]: unknown;
}

export interface AuditEntry {
    id: number | null;
    action: string;
    queueName: string | null;
    queueLabel: string | null;
    agentName: string | null;
    agentLabel: string | null;
    agentUserId: number | null;
    beforeState: AuditState | null;
    afterState: AuditState | null;
    performedByUserId: number | null;
    performedByUsername: string | null;
    ipAddress: string | null;
    createdAt: string | null;
}

export interface AuditQuery {
    customerId: number;
    limit?: number;
    beforeId?: number;
    actions?: string[];
}

export interface QueueAuditQuery extends AuditQuery {
    queueName: string;
}

function buildUrl(path: string, params: Record<string, unknown>): string {
    const url = new URL(urlApi + path);

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
            return;
        }

        // Plug reads repeated parameters as `key[]`, so list filters need the suffix.
        if (Array.isArray(value)) {
            value.forEach((item) => url.searchParams.append(`${key}[]`, String(item)));
            return;
        }

        url.searchParams.append(key, String(value));
    });

    return url.toString();
}

async function requestEntries(path: string, params: Record<string, unknown>): Promise<AuditEntry[]> {
    const token = sessionStorage.getItem('token') || '';

    const response = await fetch(buildUrl(path, params), {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
        },
    });

    const body = await response.json();

    if (!response.ok) {
        throw new Error(`Error ${response.status}: ${JSON.stringify(body)}`);
    }

    return (body?.data || []) as AuditEntry[];
}

/**
 * Operator panel audit trail for a customer. The backend filters by origin, so entries
 * written by other Picallex modules can never reach the panel.
 */
export function fetchAuditLogs(query: AuditQuery): Promise<AuditEntry[]> {
    return requestEntries('/audit', {
        customerId: query.customerId,
        limit: query.limit,
        beforeId: query.beforeId,
        actions: query.actions,
    });
}

/**
 * Operator panel audit trail scoped to a single queue. `queueName` must be the raw
 * FreeSWITCH name (including its domain), because that is what the channel reports when the
 * change happens.
 */
export function fetchQueueAuditLogs(query: QueueAuditQuery): Promise<AuditEntry[]> {
    return requestEntries('/audit/queue', {
        customerId: query.customerId,
        queueName: query.queueName,
        limit: query.limit,
        beforeId: query.beforeId,
    });
}
